import React, { useEffect, useState, useCallback, useMemo } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  CircularProgress,
  TableFooter,
  TablePagination,
} from '@material-ui/core';
import { useHistory } from 'react-router-dom';
import { format } from 'date-fns-tz';
import { startOfYear } from 'date-fns';
import { isValid } from 'date-fns/esm';
import { ptBR } from 'date-fns/locale';
import { LatLngTuple } from 'leaflet';
import FilterBar, { EngFilterOpsType } from '../../components/FilterBar';
import { jsonb as JsonbType } from '../../@types/jsonb';
import { useStyles } from './styles';
import api from '../../services/api';
import { DrawPolygonType } from '../../components/MapContent';
import Colors from '../../components/Colors';
import {
  Intervencao,
  EngineeringRequest,
  Solicitacao,
  Projeto,
  EngineeringResponsePaginated,
  Location,
  Analysis,
  Execucao,
  Envolvidos,
} from '../../@types/engineeringTypes';
import { fetchPaginatedEngineering } from '../../actions/engineeringActions';
import { getFilterDates } from '../../actions/managementActions';
import { isNull } from 'util';

interface ResponseManagement {
  uuid: string;
  datetime_begin: string;
  datetime_end: string;
  datetime_map_begin: string;
  datetime_map_end: string;
}

interface Register {
  uuid: string;
  data: {
    driverEngineeringIntervention: Intervencao;
    driverEngineeringInterventionRequest: Solicitacao;
    driverEngineeringInterventionExecution: Execucao;
  };
  geom?: {
    type: string;
  };
  occurred_from: string;
}

interface EngenhariaResponse {
  count: number;
  next: string;
  previous: string;
  results: Register[];
}

const initialJsonb: JsonbType = {
  jsonb: {
    driverEngineeringIntervention: {
      Tipo: {
        _rule_type: 'containment',
        contains: [],
      },
      Especificação: {
        _rule_type: 'containment',
        contains: [],
      },
    },
    driverEngineeringInterventionProject: {
      Status: {
        _rule_type: 'containment',
        contains: ['', ['']],
      },
    },
  },
};

const initialFilterOps: EngFilterOpsType = {
  endDate: '',
  startDate: '',
  especificacao: [],
  tipo: [],
  status: [],
  bairros: '',
  endereco: '',
  protocolo: [],
  identificador: [],
  solicitante: '',
  drawPolygon: {},
};

const loadFilterOps = (): EngFilterOpsType => {
  let filterOpsObj: EngFilterOpsType = initialFilterOps;
  const loadedFilterOps = localStorage.getItem('engFilterOps');

  if (loadedFilterOps) {
    filterOpsObj = JSON.parse(loadedFilterOps);
  }

  return filterOpsObj;
};

const loadJsonbStorage = (): JsonbType => {
  const loadedFilterOps = localStorage.getItem('engFilterOps');
  let jsonbObj: JsonbType = initialJsonb;

  if (loadedFilterOps) {
    const parsedFilters: EngFilterOpsType = JSON.parse(loadedFilterOps);

    const especificacao: Array<string | string[]> = [];
    const tipo: Array<string | string[]> = [];
    const status: Array<string | string[]> = [];

    parsedFilters.especificacao.map((item) => {
      especificacao.push(item);
      especificacao.push([item]);
    });

    parsedFilters.tipo.map((item) => {
      tipo.push(item);
      tipo.push([item]);
    });

    parsedFilters.status.map((item) => {
      status.push(item);
      status.push([item]);
    });

    jsonbObj = {
      jsonb: {
        driverEngineeringInterventionProject: {
          Status: {
            _rule_type: 'containment_multiple',
            contains: status,
          },
        },
        driverEngineeringIntervention: {
          Especificação: {
            _rule_type: 'containment_multiple',
            contains: especificacao,
          },
          Tipo: {
            _rule_type: 'containment_multiple',
            contains: tipo,
          },
        },
      },
    };
    return jsonbObj;
  }
  return jsonbObj;
};

const RegistersEngineering: React.FC = () => {
  const classes = useStyles();
  const [loading, setLoading] = useState<boolean>(true);
  const [intervencao, setIntervencao] = useState<Intervencao>({
    Tipo: '',
    Especificação: '',
    Elemento: '',
    _localId: '',
  });
  const [solicitacao, setSolicitacao] = useState<Solicitacao>({
    'Nome da Intervenção': '',
    'Data de Solicitação': '',
    'Nome do Solicitante': '',
    'Número do Protocolo': '',
    _localId: '',
    'Protocolo Informado': '',
    'Descrição da Intervenção': '',
  });
  const [execucao, setExecucao] = useState<Execucao>({ 
    'Data de Envio': '',
    'Data de Início': '',
    'Data de Conclusão': '',
    'Data de Desativação': '',
    'Nome do Responsável pela Execução': '',
    'Sem GEENG': '',
    'Sem Projeto': '',
    'Identificador da Execução da Intervenção': '',
    _localId: '',
  });
  const [localizacao, setLocalizacao] = useState<Location>({
    'Endereço 1': "",
    'Endereço 2': "",
    'Cruzamento': 'Não informado',
    "Numero Referencia": "",
    });
  const [analysis, setAnalysis] = useState<Analysis>({
    'Data de Analise': '',
    'Nome do Analista': '',
    'Detalhes da Analise': '',
    });
  const [projeto, setProjeto] = useState<Projeto>({
    'Projeto com mais de uma Intervenção': '',
    Deferido: '',
    Status: '',
    'Projeto Dependente': '',
    'Identificador do Projeto Dependente': '',
    'Identificador do Projeto': '',
    'Data de Elaboração do Projeto': '',
    Responsável: '',
    _localId: '',
    });
    const [envolvidos, setEnvolvidos] = useState<Envolvidos>({
      'Data Dois Depois': '',
      'Data Dois Antes': '',
      'Data Um Antes': '',
      'Data Um Depois': '',
      _localId: '',
    });

  const [records, setRecords] = useState<EngineeringResponsePaginated>({
    count: 0,
    next: '',
    previous: '',
    results: [],
  });
  const [dateMin, setDateMin] = useState<string>(
    `${process.env.REACT_APP_MAP_START_DEFAULT_DATE}`,
  );
  const [dateMax, setDateMax] = useState<string>(
    `${process.env.REACT_APP_MAP_END_DEFAULT_DATE}`,
  );
  const [jsonb, setJsonb] = useState<JsonbType>(loadJsonbStorage());
  const [address, setAddress] = useState<string>('');
  const [requester, setRequester] = useState<string>('');
  const [tablePage, setTablePage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(25);
  const [pageOffset, setPageOffset] = useState<number>(0);
  const [position, setPosition] = useState<LatLngTuple>([0, 0]);
  const [id_polygon, setId_polygon] = useState<{}>({
    polygon_id: loadFilterOps().bairros,
  });
  const [bboxPolygon, setBboxPolygon] = useState<{}>({});

  const [zoom, setZoom] = useState<number>(0);
  const history = useHistory();

  const [admin, setAdmin] = useState<boolean>(false);
  const [typist, setTypist] = useState<boolean>(false);
  const [tecEng, setTecEng] = useState<boolean>(false);
  const [geofi, setGeofi] = useState<boolean>(false);
  const [publicGroup, setPublicGroup] = useState<boolean>(false);
  const [startDateMap, setStartDateMap] = useState<string>('2014-01-01T00:00:00Z');
  const [endDateMap, setEndDateMap] = useState<string>('2020-06-30T23:59:59.999000Z');
  const [loadingDate, setloadingDate] = useState<boolean>(true);
  const [drawPolygon, setDrawPolygon] = useState<DrawPolygonType | {}>(
    loadFilterOps().drawPolygon,
  );

  const filterOps: EngFilterOpsType = loadFilterOps();

  const params = useMemo(() => {
    return {
      archived: 'False',
      limit: rowsPerPage,
      occurred_min: filterOps.startDate || dateMin,
      occurred_max: dateMax,
      ...jsonb,
      offset: pageOffset,
      ...id_polygon,
      ...drawPolygon,
    };
  },
  [ 
    filterOps.startDate,
    filterOps.endDate,
    jsonb,
    dateMax,
    dateMin,
    drawPolygon,
  ]);

  useEffect(() => {
    const groups = localStorage.getItem('groups')?.split(' ');
    if (groups) {
      groups.forEach((gr: string) => {
        if (gr === 'public') {
          setPublicGroup(true);
        } else if (gr === 'digitador') {
          setTypist(true);
        } else if (gr === 'tec_engenharia') {
          setTecEng(true);
        } else if (gr === 'geofi') {
          setGeofi(true);
        } else if (gr === 'admin') {
          setAdmin(true);
        }
      });
    }
  }, []);

  if (publicGroup && !(admin || geofi || tecEng || typist)) {
    history.push('/mapa/sinistros');
  }

  const navigate = (pathId: string) => {
    history.push(pathId);
  };

  const fetchManagementDate = useCallback(async () => {
    setloadingDate(true);
    try {
      const dates = await getFilterDates(filterOps.startDate, filterOps.endDate);
      setDateMin(dates.dateMin);
      setDateMax(dates.dateMax);
      setStartDateMap(dates.startDateMap);
      setEndDateMap(dates.endDateMap);
    } catch (error){
      console.log(error);
    } finally {
      setloadingDate(false);
    }

  }, [filterOps.endDate, filterOps.startDate]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try{
      const { data } = await fetchPaginatedEngineering({params});
      setRecords(data);
    } catch(error) {
      console.log("Error");
    } finally {
      setLoading(false);
    }
  }, [rowsPerPage, dateMin, dateMax, jsonb, pageOffset, id_polygon, drawPolygon]);

  useEffect(() => {
    fetchManagementDate();
  }, [fetchManagementDate]);

  useEffect(() => {
    if (!loadingDate) {
      fetchData();
    }
  }, [loadingDate, fetchData]);

  const handleChangePage = (event: unknown, newPage: number) => {
    newPage < tablePage
      ? setPageOffset((prev) => prev - rowsPerPage)
      : setPageOffset((prev) => prev + rowsPerPage);

    setTablePage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPageOffset(0);
    setTablePage(0);
  };

  const toDate = (date: string): String => {
    if (isValid(new Date(date))) {
      const newDate = format(new Date(date), 'dd MMMM, yyyy, HH:mm:ss', {
        locale: ptBR,
      });
      return newDate;
    }
    return '';
  };
  return (
    <>
      <FilterBar
        filterJson={setJsonb}
        dateMaxSet={setDateMax}
        dateMinSet={setDateMin}
        setPositionSearch={setPosition}
        zoom={setZoom}
        id_boundary={setId_polygon}
        bbox_boundary={setBboxPolygon}
        routeTo="engenharia"
        startDateMap={startDateMap}
        endDateMap={endDateMap}
        drawPolygon={drawPolygon}
        setDrawPolygon={setDrawPolygon}
        address={address}
        setAddress={setAddress}
        requester={requester}
        setRequester={setRequester}
      />
      {loading ? (
        <div className={classes.loadingContainer}>
          <CircularProgress className={classes.loadingSpinner} size={60} thickness={5} />
        </div>
      ) : (
        <TableContainer className={classes.tableContainer}>
          <Table className={classes.table}>
            <TableHead className={classes.tableHead}>
              <TableRow>
                <TableCell
                  style={{ color: Colors.tertiary }}
                  className={classes.responsive}
                >
                  Data e Hora Solicitação
                </TableCell>
                <TableCell
                  style={{ color: Colors.tertiary }}
                  className={classes.responsive}
                >
                  Data e Hora Conclusão
                </TableCell>
                <TableCell style={{ color: Colors.tertiary }}>
                  Número do Processo
                </TableCell>
                <TableCell style={{ color: Colors.tertiary }}>
                  Tipo da Intervenção
                </TableCell>
                <TableCell style={{ color: Colors.tertiary }}>Especificação</TableCell>
                <TableCell style={{ color: Colors.tertiary }}>Funções</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {records.results.map((value) => (
                <TableRow key={value.uuid} hover>
                  <TableCell className={classes.responsive}>
                    {`${toDate(value.occurred_from)}`}
                  </TableCell>
                  <TableCell className={classes.responsive}>
                    {value.data.driverEngineeringInterventionExecution?.['Data de Conclusão'] !== null ?
                    (`${toDate(
                      value.data.driverEngineeringInterventionExecution?.[
                        'Data de Conclusão'
                      ],
                    )}`) :
                    'N/A'
                  }
                  </TableCell>
                  <TableCell width={300}>
                    {
                      value.data.driverEngineeringInterventionRequest[
                        'Número do Protocolo'
                      ]
                    }
                  </TableCell>
                  <TableCell width={300}>
                    {value.data.driverEngineeringIntervention.Tipo}
                  </TableCell>
                  <TableCell width={300}>
                    {value.data.driverEngineeringIntervention.Especificação}
                  </TableCell>
                  <TableCell>
                    <div className={classes.buttonsCol}>
                      <Button
                        size="small"
                        variant="outlined"
                        className={classes.button}
                        onClick={() => navigate(`/editar/engenharia/${value.uuid}`)}
                      >
                        Editar
                      </Button>
                      <a
                        href={`/registro/engenharia/${value.uuid}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ textDecoration: 'none', color: 'black' }}
                      >
                        <Button
                          size="small"
                          variant="outlined"
                          className={classes.button}
                        >
                          Exibir
                        </Button>
                      </a>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
            <TableFooter>
              <TableRow>
                <TablePagination
                  rowsPerPageOptions={[25, 50]}
                  rowsPerPage={rowsPerPage}
                  count={records.count !== null ? records.count : 0}
                  page={tablePage}
                  SelectProps={{
                    inputProps: { 'aria-label': 'rows per page' },
                  }}
                  labelRowsPerPage={<span>Linhas por página</span>}
                  labelDisplayedRows={({ from, to, count }) =>
                    `${from}-${to} de ${count}`
                  }
                  onChangePage={handleChangePage}
                  onChangeRowsPerPage={handleChangeRowsPerPage}
                />
              </TableRow>
            </TableFooter>
          </Table>
        </TableContainer>
      )}
    </>
  );
};

export default RegistersEngineering;
